import React from "react";
import {
  wordGeometry,
  syllableGeometry,
  CELL_W,
  PAD,
  BAND_H,
} from "../../lib/sitelenNasin";

const INK = "var(--sn-ink, #1c1e1a)";
const JOINER = "var(--sn-joiner, #a9b0a4)";

// Natural size caps the glyph; max-width lets it shrink fluidly with its
// container on small screens (height: auto keeps the aspect ratio).
const FLUID = { maxWidth: "100%", height: "auto" };

// One syllable stroke in its own little SVG cell.
export function SyllableGlyph({ spec, scale = 1, strokeWidth = 3, color = INK }) {
  const g = syllableGeometry(spec);
  return (
    <svg
      viewBox={`0 0 ${g.width} ${g.height}`}
      width={g.width * scale}
      height={g.height * scale}
      style={FLUID}
      aria-hidden
    >
      <path
        d={g.d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// A whole word: bands stacked into blocks, joiner dashes between blocks.
export function WordGlyph({ word, scale = 1, strokeWidth = 3, boxes = false }) {
  const g = wordGeometry(word);
  if (!g) return null;
  const boxRects = [];
  if (boxes) {
    for (let i = 0; i < g.blocks.length; i += 1) {
      boxRects.push(
        <rect
          key={i}
          x={i * (CELL_W + 8)}
          y={0}
          width={CELL_W}
          height={g.height}
          fill="none"
          stroke="var(--sn-line, #d8dcd3)"
          strokeWidth="1"
        />
      );
    }
  }
  return (
    <svg
      viewBox={`0 0 ${g.width} ${g.height}`}
      width={g.width * scale}
      height={g.height * scale}
      style={FLUID}
      aria-hidden
    >
      {boxRects}
      {g.paths.map((p, i) => (
        <path
          key={i}
          d={p.d}
          fill="none"
          stroke={p.joiner ? JOINER : INK}
          strokeWidth={p.joiner ? 2 : strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

// A run of toki pona text: valid words drawn as glyphs, invalid words shown
// in red latin, punctuation passed through.
export function ScriptText({ text, scale = 0.9, strokeWidth = 3, labels = true, boxes = false }) {
  const tokens = text.trim() ? text.trim().split(/\s+/) : [];
  if (!tokens.length) return null;
  return (
    <div className="sn-script-row">
      {tokens.map((tok, i) => {
        const m = tok.match(/^([^a-zA-Z]*)([a-zA-Z]*)([^a-zA-Z]*)$/);
        const core = m ? m[2] : tok;
        const tail = m ? m[3] : "";
        if (!core) {
          return (
            <span key={i} className="sn-punct">
              {tok}
            </span>
          );
        }
        const g = wordGeometry(core);
        if (!g) {
          return (
            <span key={i} className="sn-bad" title="not valid toki pona phonology">
              {tok}
            </span>
          );
        }
        return (
          <div key={i} className="sn-script-word">
            <div className="sn-script-word-inner">
              <WordGlyph word={core} scale={scale} strokeWidth={strokeWidth} boxes={boxes} />
              {tail && <span className="sn-punct sn-punct-tail">{tail}</span>}
            </div>
            {labels && <div className="sn-latin">{core}</div>}
          </div>
        );
      })}
    </div>
  );
}

// Fixed-height practice card wrapper so quiz glyphs of different words
// don't jump the layout around.
export function GlyphCard({ children, minHeight = PAD * 2 + BAND_H + 96 }) {
  return (
    <div className="sn-glyph-card" style={{ minHeight }}>
      {children}
    </div>
  );
}
